/**
 * Blueprint Registry
 * Loads blueprint data and creates GameObjects with runtime inheritance resolution
 */

import type { BlueprintData, RawBlueprintDef, BlueprintDef, PartDef } from './types';
import { Events } from './types';
import { PartRegistry } from './PartRegistry';
import { GameObject } from './GameObject';

/**
 * Resolved blueprint with inheritance chain flattened
 * Created on-demand when objects are instantiated
 */
interface ResolvedBlueprint {
  name: string;
  /** Full inheritance chain from this blueprint to root */
  inheritanceChain: string[];
  /** All parts (own + inherited), with child overriding parent */
  parts: PartDef[];
  /** All builders (own + inherited), with child overriding parent */
  builders: PartDef[];
  /** All tags (own + inherited), with child overriding parent */
  tags: Record<string, string>;
  /** All stats (own + inherited), with child overriding parent */
  stats: Record<string, number>;
}

/**
 * Registry for blueprints
 * Stores raw blueprint data and resolves inheritance on-demand
 */
export class BlueprintRegistry {
  private rawBlueprints = new Map<string, RawBlueprintDef>();
  private resolvedCache = new Map<string, ResolvedBlueprint>();
  readonly partRegistry = new PartRegistry();

  /**
   * Load blueprints from a URL
   */
  async load(url: string): Promise<void> {
    const response = await fetch(url);
    const data: BlueprintData = await response.json();
    this.loadFromData(data);
  }

  /**
   * Load blueprints from already-fetched data
   */
  loadFromData(data: BlueprintData): void {
    this.rawBlueprints.clear();
    this.resolvedCache.clear();

    for (const [name, def] of Object.entries(data.blueprints)) {
      this.rawBlueprints.set(name, def);
    }

    console.log(`BlueprintRegistry: Loaded ${this.rawBlueprints.size} blueprints`);
  }

  /**
   * Get the raw (unresolved) blueprint definition
   * This shows only what's defined directly on this blueprint
   */
  getRaw(name: string): RawBlueprintDef | undefined {
    return this.rawBlueprints.get(name);
  }

  /**
   * Get a resolved blueprint with inheritance flattened
   * Caches the result for performance
   */
  getResolved(name: string): ResolvedBlueprint | undefined {
    // Check cache first
    const cached = this.resolvedCache.get(name);
    if (cached) return cached;

    const raw = this.rawBlueprints.get(name);
    if (!raw) return undefined;

    const resolved = this.resolveInheritance(name, raw);
    this.resolvedCache.set(name, resolved);
    return resolved;
  }

  /**
   * Resolve inheritance for a blueprint
   * Walks up the inheritance chain and merges properties
   */
  private resolveInheritance(name: string, raw: RawBlueprintDef): ResolvedBlueprint {
    // Build inheritance chain (child to root)
    const chain: string[] = [name];
    const blueprintsInChain: RawBlueprintDef[] = [raw];

    let current = raw;
    while (current.inherits) {
      const parent = this.rawBlueprints.get(current.inherits);
      if (!parent) {
        // Parent not found - might be filtered out or missing
        break;
      }
      chain.push(current.inherits);
      blueprintsInChain.push(parent);
      current = parent;
    }

    // Merge from root to child (so child overrides parent)
    // Start with empty collections
    const partMap = new Map<string, PartDef>();
    const builderMap = new Map<string, PartDef>();
    let tags: Record<string, string> = {};
    let stats: Record<string, number> = {};

    // Process from root (end of array) to child (start of array)
    for (let i = blueprintsInChain.length - 1; i >= 0; i--) {
      const bp = blueprintsInChain[i];

      // Apply removeParts first (removes inherited parts)
      if (bp.removeParts) {
        for (const partName of bp.removeParts) {
          partMap.delete(partName);
        }
      }

      // Apply removeBuilders (removes inherited builders)
      if (bp.removeBuilders) {
        for (const builderName of bp.removeBuilders) {
          builderMap.delete(builderName);
        }
      }

      // Parts: child params merge with parent params (child overrides individual params)
      for (const part of bp.parts) {
        const existing = partMap.get(part.name);
        if (existing) {
          // Merge params: child overrides parent
          partMap.set(part.name, {
            name: part.name,
            params: { ...existing.params, ...part.params },
          });
        } else {
          partMap.set(part.name, part);
        }
      }

      // Builders: child params merge with parent params (child overrides individual params)
      for (const builder of bp.builders) {
        const existing = builderMap.get(builder.name);
        if (existing) {
          // Merge params: child overrides parent
          builderMap.set(builder.name, {
            name: builder.name,
            params: { ...existing.params, ...builder.params },
          });
        } else {
          builderMap.set(builder.name, builder);
        }
      }

      // Tags: child overrides parent
      tags = { ...tags, ...bp.tags };

      // Stats: child overrides parent
      stats = { ...stats, ...bp.stats };
    }

    return {
      name,
      inheritanceChain: chain,
      parts: Array.from(partMap.values()),
      builders: Array.from(builderMap.values()),
      tags,
      stats,
    };
  }

  /**
   * Get a blueprint by name (resolved, for backwards compatibility)
   */
  get(name: string): BlueprintDef | undefined {
    const resolved = this.getResolved(name);
    if (!resolved) return undefined;

    return {
      name: resolved.name,
      parts: resolved.parts,
      tags: resolved.tags,
      stats: resolved.stats,
    };
  }

  /**
   * Check if a blueprint exists
   */
  has(name: string): boolean {
    return this.rawBlueprints.has(name);
  }

  /**
   * Get all blueprint names
   */
  getBlueprintNames(): string[] {
    return Array.from(this.rawBlueprints.keys());
  }

  /**
   * Get the inheritance chain for a blueprint
   * Returns array from child to root ancestor
   */
  getInheritanceChain(name: string): string[] {
    const resolved = this.getResolved(name);
    return resolved?.inheritanceChain ?? [];
  }

  /**
   * Check if a blueprint inherits from another (directly or indirectly)
   */
  inheritsFrom(name: string, ancestor: string): boolean {
    const chain = this.getInheritanceChain(name);
    return chain.includes(ancestor);
  }

  /**
   * Create a GameObject from a blueprint
   */
  createObject(blueprintName: string): GameObject {
    const resolved = this.getResolved(blueprintName);
    if (!resolved) {
      throw new Error(`Blueprint not found: ${blueprintName}`);
    }

    const obj = new GameObject(blueprintName);

    // Copy tags to properties
    for (const [key, value] of Object.entries(resolved.tags)) {
      obj.setProperty(key, value);
    }

    // Copy stats to int properties
    for (const [key, value] of Object.entries(resolved.stats)) {
      obj.setIntProperty(key, value);
    }

    // Instantiate parts
    for (const partDef of resolved.parts) {
      const part = this.partRegistry.create(partDef.name, partDef.params);
      if (part) {
        obj.addPart(part);
      }
    }

    // Instantiate builders (run after parts)
    for (const builderDef of resolved.builders) {
      const builder = this.partRegistry.create(builderDef.name, builderDef.params);
      if (builder) {
        obj.addPart(builder);
      }
    }

    // Fire creation event
    obj.fireEvent(Events.OBJECT_CREATED);

    return obj;
  }
}
